// Class builders shared by the UI components and by links styled like them.

/*
 * The builder's buttons. primary is the default call to action; accent
 * (terracotta) is kept for publishing and paying; secondary and ghost are
 * quieter; danger confirms something destructive. Keyboard focus is an ink
 * outline everywhere.
 */
const variants = {
  primary: "bg-hazumu-ink font-semibold text-hazumu-white hover:bg-hazumu-terracotta",
  accent: "bg-hazumu-terracotta font-semibold text-hazumu-white hover:bg-hazumu-ink",
  secondary: "border border-hazumu-border bg-hazumu-white font-medium hover:bg-hazumu-canvas",
  ghost: "font-medium hover:bg-hazumu-canvas",
  danger: "bg-hazumu-danger font-semibold text-hazumu-white hover:bg-hazumu-ink",
} as const;

/** Text colour for the unfilled variants, so exactly one colour applies. */
const quietText = {
  secondary: "text-hazumu-ink",
  ghost: "text-hazumu-muted hover:text-hazumu-ink",
} as const;

const sizes = {
  sm: "min-h-8 px-3 py-1.5 text-xs",
  md: "min-h-10 px-4 py-2 text-sm",
  lg: "min-h-12 px-5 py-3 text-sm",
} as const;

export type ButtonOptions = {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  /** Red text for destructive secondary or ghost actions. */
  destructive?: boolean;
  fullWidth?: boolean;
  /** Pill-shaped, for the marketing pages. */
  pill?: boolean;
};

/** Button classes, for links that should look like buttons. */
export const buttonClass = ({
  variant = "secondary",
  size = "md",
  destructive = false,
  fullWidth = false,
  pill = false,
}: ButtonOptions = {}) =>
  [
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
    variants[variant],
    variant === "secondary" || variant === "ghost"
      ? destructive
        ? "text-hazumu-danger"
        : quietText[variant]
      : "",
    sizes[size],
    pill ? "rounded-full" : size === "sm" ? "rounded-lg" : "rounded-xl",
    fullWidth ? "w-full" : "",
  ].join(" ");

/*
 * Form controls. md is for panels and sheets, lg for onboarding and sign-in.
 * Every control shares one focus treatment: a terracotta border and a soft
 * clay ring.
 */
const inputSizes = {
  md: "rounded-xl px-3 py-2 text-base sm:text-sm",
  lg: "rounded-xl px-4 py-3 text-base",
} as const;

export type InputSize = keyof typeof inputSizes;

export const inputClass = (size: InputSize = "md") =>
  `w-full min-w-0 border border-hazumu-border bg-hazumu-white text-hazumu-ink outline-none transition placeholder:text-hazumu-muted focus:border-hazumu-terracotta focus:ring-2 focus:ring-hazumu-clay/60 disabled:opacity-60 ${inputSizes[size]}`;


/** An inline link in running text: underlined ink, quieter on hover. */
export const textLinkClass =
  "rounded-sm text-hazumu-ink underline underline-offset-2 transition-colors hover:text-hazumu-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink";
